import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import DropdownInput from 'components/DropdownInput';
import DropdownAutocomplete from 'components/DropdownAutocomplete';
import LoadingSpinner from 'components/LoadingSpinner';
import AdvancedDashboardSearch from 'components/Widget/components/menus/MainSettings/components/AdvancedDashboardSearch';
import { ThemeButton } from 'components/shared';
import useApi, { GET_DEFAULT_MASTER_OBJECT_BENCHMARKS } from 'hooks/useApi';
import Collapse from '@material-ui/core/Collapse';
import { makeStyles } from '@material-ui/core';
import { masterObjectTypes, generateEmptyRecord } from '../../content';

const useStyles = makeStyles({
  heading: {
    marginBottom: '10px',
    marginTop: '0',
    opacity: '0.7'
  }
});

const MasterObjectSection = ({
  dispatchUpdate,
  updateAction,
  objectState,
  masterObjectTypeIdLookupState,
  masterObjects,
  dispatchMasterObject
}) => {
  const classes = useStyles();
  const [state, sendRequest] = useApi();
  const { data: response, isLoading: isRequestLoading, isError } = state;

  const { masterObjectTypeId, masterObjectId } = objectState;
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [isMasterObjectInherited] = useState(!!masterObjectTypeId && !!masterObjectId);

  const { isLoading } = masterObjectTypeIdLookupState;

  const handleMasterObjectIdChange = selected => {
    dispatchUpdate({
      type: updateAction,
      payload: { masterObjectId: selected.value }
    });

    const params =
      selected.value && GET_DEFAULT_MASTER_OBJECT_BENCHMARKS({ masterObjectId: selected.value });
    sendRequest(params);
  };

  const handleFormStateChange = event => {
    dispatchUpdate({
      type: updateAction,
      payload: { masterObjectTypeId: event.target.value }
    });
  };

  const toggleAdvancedSearch = () => {
    setIsAdvancedSearch(prevState => !prevState);
  };

  const onMasterObjectChanged = updatedBenchmark => {
    const dropdownOptionExists = masterObjects.some(
      fb => fb.label === updatedBenchmark.name && fb.value === updatedBenchmark.timeSerieId
    );
    const itemToAdd = {
      label: updatedBenchmark.name,
      value: updatedBenchmark.id || updatedBenchmark.timeSerieId
    };

    if (!dropdownOptionExists) {
      dispatchMasterObject({
        type: 'ADD_ADVANCED_VALUE',
        payload: { data: itemToAdd, id: masterObjectTypeId }
      });
    }

    dispatchUpdate({
      type: updateAction,
      payload: { masterObjectId: itemToAdd.value }
    });

    const params = GET_DEFAULT_MASTER_OBJECT_BENCHMARKS({ masterObjectId: itemToAdd.value });
    sendRequest(params);
  };

  useEffect(() => {
    if (isRequestLoading || isError) return;

    if (response) {
      const { data, masterObjectTimeSeriesSetId } = response;
      const formattedBenchmarks = generateEmptyRecord([...data]);
      dispatchUpdate({
        type: updateAction,
        payload: {
          benchmarks: formattedBenchmarks,
          masterObjectTimeSeriesSetId
        }
      });
    }
  }, [dispatchUpdate, isError, isRequestLoading, response, updateAction]);

  return (
    <>
      <Grid item xs={12} md={4}>
        <h3 className={classes.heading}>
          {isMasterObjectInherited ? 'Override Object Type' : 'Object Type *'}
        </h3>
        <DropdownInput
          selected={masterObjectTypeId}
          options={masterObjectTypes}
          onSelect={handleFormStateChange}
          displayEmpty
          label="Choose..."
          name="masterObjectTypeId"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <h3 className={classes.heading}>
          {isMasterObjectInherited ? 'Override Master Object' : 'Master Object *'}
        </h3>
        {masterObjects && !isLoading && (
          <DropdownAutocomplete
            selected={masterObjectId}
            options={masterObjects}
            onSelect={handleMasterObjectIdChange}
            minWidth="220px"
            type="seriesType"
            listBoxHeight="360px"
            name="masterObjectId"
            label="Choose..."
          />
        )}
        {isLoading && <LoadingSpinner p={0} size={40} />}
      </Grid>

      <Grid item xs={2}>
        <ThemeButton
          themeColor={isAdvancedSearch ? 'grey' : 'accent'}
          size="medium"
          onClick={toggleAdvancedSearch}
          fullWidth
        >
          {isAdvancedSearch ? 'Hide' : 'Advanced'}
        </ThemeButton>
      </Grid>

      <Collapse in={isAdvancedSearch}>
        <Grid item xs={12}>
          <Box bgcolor="#fff" borderRadius={4} width="100%">
            <AdvancedDashboardSearch
              onAccepted={onMasterObjectChanged}
              clearAdvancedView={toggleAdvancedSearch}
            />
          </Box>
        </Grid>
      </Collapse>
    </>
  );
};

MasterObjectSection.propTypes = {
  objectState: PropTypes.shape({
    masterObjectId: PropTypes.number,
    masterObjectTypeId: PropTypes.number,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        benchmarkId: PropTypes.number,
        name: PropTypes.string
      })
    )
  }),
  dispatchUpdate: PropTypes.func,
  dispatchMasterObject: PropTypes.func,
  updateAction: PropTypes.string.isRequired,
  masterObjectTypeIdLookupState: PropTypes.shape({
    isLoading: PropTypes.bool
  }).isRequired,
  masterObjects: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.number,
      value: PropTypes.string
    })
  )
};

MasterObjectSection.defaultProps = {
  objectState: {},
  dispatchUpdate: null,
  dispatchMasterObject: null,
  masterObjects: []
};
export default MasterObjectSection;
